import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';

import { notifications } from '../api/client';
import type { Notification } from '../api/types';
import { describeNotification, relativeTime } from '../lib/notifications';

const ICON_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Unread badge is polled independently so the count stays cheap (60 s). */
const POLL_MS = 60_000;
const POPOVER_LIMIT = 15;

function IconClock() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" {...ICON_STROKE} />
      <path d="M12 8v4.5l3 1.8" {...ICON_STROKE} />
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <path d="M19.5 12a7.5 7.5 0 1 1-2.4-5.5" {...ICON_STROKE} />
      <path d="M19.6 4.6v3.6H16" {...ICON_STROKE} />
    </svg>
  );
}

function IconStatus() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <path d="M4 12.5 9 17.5 20 6.5" {...ICON_STROKE} />
    </svg>
  );
}

function IconBell() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M6 10a6 6 0 0 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 14 6 10Z" {...ICON_STROKE} />
      <path d="M10 18.5a2 2 0 0 0 4 0" {...ICON_STROKE} />
    </svg>
  );
}

const KIND_ICON = { clock: <IconClock />, refresh: <IconRefresh />, status: <IconStatus /> } as const;

function BellRow({ notification, onOpen }: { notification: Notification; onOpen: () => void }) {
  const copy = describeNotification(notification);
  const unread = notification.readAt === null;
  const label = relativeTime(notification.dueAt ?? notification.createdAt) ?? '';

  const body = (
    <>
      <span className={`bell-row__icon bell-row__icon--${copy.severity}`} aria-hidden="true">
        {KIND_ICON[copy.icon]}
      </span>
      <span className="bell-row__body">
        <span className="bell-row__title">
          {!unread ? <span className="bell-row__dot bell-row__dot--read" aria-hidden="true" /> : null}
          {copy.title}
        </span>
        {copy.detail ? <span className="bell-row__detail">{copy.detail}</span> : null}
      </span>
      <span className="bell-row__time">{label}</span>
    </>
  );

  const className = `bell-row${unread ? ' bell-row--unread' : ''}`;

  // A subscription link keeps the popover cheap: clicking through lands on the
  // filtered subscriptions list, which is where the fix happens anyway.
  if (notification.subscription) {
    return (
      <li>
        <Link
          className={className}
          to={`/subscriptions?q=${encodeURIComponent(notification.subscription.name)}`}
          onClick={onOpen}
        >
          {body}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <button type="button" className={className} onClick={onOpen}>
        {body}
      </button>
    </li>
  );
}

/**
 * The in-app bell (Phase 7): unread badge polled every minute, popover with the
 * latest entries, mark-read on click and a read-all action.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const count = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: ({ signal }) => notifications.unreadCount(signal),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: POLL_MS,
    retry: 1,
  });

  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: ({ signal }) => notifications.list({ limit: POPOVER_LIMIT }, signal),
    enabled: open,
    staleTime: 15_000,
    retry: 1,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markRead = useMutation({ mutationFn: (id: string) => notifications.markRead(id), onSuccess: invalidate });
  const markAllRead = useMutation({ mutationFn: () => notifications.markAllRead(), onSuccess: invalidate });

  // Close on outside click / Escape, like the global search popover.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const items = list.data?.items ?? [];
  const unread = count.data?.count ?? 0;

  return (
    <div className="bell" ref={containerRef}>
      <button
        type="button"
        className="icon-btn"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        <IconBell />
        {unread > 0 ? <span className="bell__badge">{unread > 9 ? '9+' : unread}</span> : null}
      </button>

      {open ? (
        <div className="bell__panel" role="dialog" aria-label="Notifications">
          <header className="bell__head">
            <span className="bell__head-title">
              Notifications
              {unread > 0 ? <span className="bell__count">{unread} unread</span> : null}
            </span>
            <button
              type="button"
              className="bell__head-action"
              onClick={() => markAllRead.mutate()}
              disabled={unread === 0 || markAllRead.isPending}
            >
              Mark all read
            </button>
          </header>

          {list.isLoading ? (
            <div className="bell__empty">Loading…</div>
          ) : items.length === 0 ? (
            <div className="bell__empty">Nothing to see — no expiries, renewals or status changes.</div>
          ) : (
            <ul className="bell__list">
              {items.map((notification) => (
                <BellRow
                  key={notification.id}
                  notification={notification}
                  onOpen={() => {
                    if (notification.readAt === null) markRead.mutate(notification.id);
                    setOpen(false);
                  }}
                />
              ))}
            </ul>
          )}

          <footer className="bell__foot">
            <span>
              {items.length > 0 ? 'Scroll for older entries · ' : ''}
              Expiries ≤ 14 d · renewals ≤ 7 d · scanned daily 09:00 MSK
            </span>
          </footer>
        </div>
      ) : null}
    </div>
  );
}
